import { Injectable, OnDestroy, OnInit } from "@angular/core";

import { LoggerService } from "@bfx/logger";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxSoundsService } from "app/bfx-common/services/bfx-sounds.service";
import {
  BfxBlotterFilter,
  ExtendedOrderTypeIdentifier,
  FilterType,
} from "../../bfx-blotter-filter/bfx-blotter-filter.constants";

import * as SettingsConst from "../model/bfx-settings-constants";

import { Observable, Subject, Subscription } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BFXAppNotificationType,
  BFXNotificationEventType,
  BFXNotificationType,
  ProfileSettingKeys,
} from "shared/cubiq-services";
import { Sound } from "app/bfx-common/models/sound.model";
import { BfxPopupNotificatonSettingsService } from "app/bfx-common/services/bfx-popup-notifications-settings.service";
import { BfxAppNotificatonSettingsService } from "app/bfx-common/services/bfx-app-notifications-settings.service";
import { BfxPopupEventSetting } from "app/bfx-user-settings/model/bfx-popup-event-setting";
import { BfxAppEventSetting } from "app/bfx-user-settings/model/bfx-app-event-setting";

const logger = LoggerService.for(__dirname);

export const POPUP_KEY: string = ProfileSettingKeys.userProfileSettingsNotifPopup;
export const POPUP_BMARK_KEY: string = ProfileSettingKeys.userProfileSettingsNotifBmarkPopup;
export const APP_KEY: string = ProfileSettingKeys.userProfileSettingsNotifApp;
export const APP_BMARK_KEY: string = ProfileSettingKeys.userProfileSettingsNotifBmarkApp;

export type SettingFilterType = {
  name: string;
  id: string;
  blotterType: CubiqTS.BlotterType;
  orderFilterType: ExtendedOrderTypeIdentifier;
};

export type SettingAudioType = {
  label: string;
  value: string;
};

/** used to determine the list of eligible filters */
enum FilterClassType {
  LIMIT_FILTER,
  ALGO_FILTER,
  BM_FILTER,
  TRADE_FILTER,
  DEALER_INTERVENTION,
  NONE,
}

/**
 * Common settings for all notification setting views and services.
 * As this service updates all filter lists on any filter change so it should only be used as a local settings service,
 * not registered in the root injector. If registered in the root, then all filter changes would be processed here even when settings is closed.
 * As active filters can only be gotten asynchronously in a promise, there are limited options here at the moment.
 */
@Injectable()
export class BfxUserNotificationSettingsService implements OnDestroy {
  public soundsList: SettingAudioType[] = [];

  private filtersList: SettingFilterType[] = [];

  private filterGroupMap: Map<FilterClassType, SettingFilterType[]> = new Map();

  private readonly popupSettings: BfxPopupEventSetting[] = [];
  private readonly applicationSettings: BfxAppEventSetting[] = [];

  public ready: Promise<any>;

  public audioDisabled: boolean = false;

  private updatedPopupSettingsMap: Map<BFXNotificationType, BfxPopupEventSetting> = new Map();
  private updatedAppSettingsMap: Map<BFXAppNotificationType, BfxAppEventSetting> = new Map();

  private filtersUpdatedSubject = new Subject<void>();
  public $filtersUpdated: Observable<void> = this.filtersUpdatedSubject.asObservable();

  private subscriptions: Subscription[] = [];

  constructor(
    private _filterService: BfxBlotterFilterService,
    private soundsService: BfxSoundsService,
    private popSettingsService: BfxPopupNotificatonSettingsService,
    private appSettingsService: BfxAppNotificatonSettingsService,
  ) {
    this.initialize();
    logger.out.info("uns-s - Service created");
  }

  ngOnDestroy(): void {
    logger.out.info("uns-s - Service destroyed");
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  public initialize(): void {
    this.ready = Promise.all([
      this.populateSoundsList(),
      this.populateFilterList(),
      this.initializeSettings(),
    ]).then(([soundsList, filtersList]) => {
      if (soundsList) {
        this.soundsList = soundsList;
        this.soundsList.sort(SettingsConst.OBJECT_SORT);
        this.soundsList.unshift(SettingsConst.NONE_SOUND);
      }

      if (filtersList) {
        this.filtersList = filtersList;
        this.filtersList.sort(SettingsConst.FILTER_SORT);
        this.initializeFilterGroupMap();
        let subscription;
        subscription = this._filterService.listenForFilterDeleting().subscribe((ids) => {
          // On a filter change we update all the filter groups then we notify change subscribers
          this.updateAllFilters().then(() => this.filtersUpdatedSubject.next());
        });
        this.subscriptions.push(subscription);
        subscription = this._filterService.modelUpdateSubject.asObservable().subscribe((ids) => {
          // On a filter change we update all the filter groups then we notify change subscribers
          this.updateAllFilters().then(() => this.filtersUpdatedSubject.next());
        });
        this.subscriptions.push(subscription);
      }
    });
  }

  private updateAllFilters() {
    return this.populateFilterList().then((filtersList) => {
      logger.out.info("uns-s - Updating filters list");
      if (filtersList) {
        this.filtersList = filtersList;
        this.filtersList.sort(SettingsConst.FILTER_SORT);
        this.initializeFilterGroupMap();
      }
    });
  }

  protected initializeSettings(): Promise<void[]> {
    return Promise.all([this.initializePopupSettings(), this.initializeApplicationSettings()]);
  }

  /**
   * Read all general and bmark popup notification settings and adapt them. It will
   * set the global audioDisabled flag from the first setting.
   * Once primed, it will listen for all subsequent setting changes
   * @returns
   */
  protected initializePopupSettings(): Promise<any> {
    this.popupSettings.length = 0;
    this.updatedPopupSettingsMap.clear();
    const updateFn = (notifSettings) => {
      notifSettings.forEach((element) => {
        const setting = this.createPopupSetting(element);
        this.popupSettings.push(setting);
      });
      if (notifSettings.length > 0) {
        this.audioDisabled = notifSettings[0].disableAllAudio;
      }
    };
    return Promise.all([
      this.popSettingsService.getPopupNotificationsSettings().then(updateFn),
      this.popSettingsService.getBmarkPopupNotificationsSettings().then(updateFn),
    ]).then(() => {
      // Once we've primed we can set up listeners for changes
      let subscription = this.popSettingsService
        .getPopupSettingsUpdateStream()
        .subscribe((notifSettings) => this.processPopupUpdates(notifSettings));
      this.subscriptions.push(subscription);
      subscription = this.popSettingsService
        .getPopupBmarkSettingsUpdateStream()
        .subscribe((notifSettings) => this.processPopupUpdates(notifSettings));
      this.subscriptions.push(subscription);
    });
  }

  /**
   * Read all general and bmark application notification settings and adapt them. It will
   * set the global audioDisabled flag from the first setting.
   * Once primed, it will listen for all subsequent setting changes
   * @returns
   */
  protected initializeApplicationSettings(): Promise<any> {
    this.applicationSettings.length = 0;
    this.updatedAppSettingsMap.clear();

    const updateFn = (notifSettings) => {
      notifSettings.forEach((element) => {
        const setting = this.createAppSetting(element);
        this.applicationSettings.push(setting);
      });
    };
    return Promise.all([
      this.appSettingsService.getAppNotificationsSettings().then(updateFn),
      this.appSettingsService.getBmarkAppNotificationsSettings().then(updateFn),
    ]).then(() => {
      // Once we've primed we can set up listeners for changes
      let subscription = this.appSettingsService
        .listenForSettingsUpdates()
        .subscribe((notifSettings) => this.processAppUpdates(notifSettings));
      this.subscriptions.push(subscription);
      subscription = this.appSettingsService
        .listenForBmarkSettingsUpdates()
        .subscribe((notifSettings) => this.processAppUpdates(notifSettings));
      this.subscriptions.push(subscription);
    });
  }

  protected createPopupSetting(element): BfxPopupEventSetting {
    const setting: BfxPopupEventSetting = new BfxPopupEventSetting(
      element.type,
      element.eventIdentifier,
      element.enabled,
      Object.assign(new Sound("", ""), element.audio),
      element.disableAllAudio,
      Object.assign(new BfxBlotterFilter("0", "", "", 0, ""), element.filter),
      element.blotterType,
      element.isBmark,
    );
    return setting;
  }

  protected createAppSetting(element): BfxAppEventSetting {
    const settings: BfxAppEventSetting = new BfxAppEventSetting(
      element.type,
      element.eventIdentifier,
      element.enabled,
      element.returnOnLogin,
      Object.assign(new Sound("", ""), element.audio),
      element.disableAllAudio,
      Object.assign(new BfxBlotterFilter("0", "", "", 0, ""), element.filter),
      element.blotterType,
      element.isBmark,
    );
    return settings;
  }

  /**
   * Update all our stored popup settings with the new setting values
   * @param settings
   */
  private processPopupUpdates(settings: BfxPopupEventSetting[]) {
    settings.forEach((element) => {
      const newSetting = this.createPopupSetting(element);
      const index = this.popupSettings.findIndex((setting) => setting.type === newSetting.type);
      if (index != -1) {
        this.popupSettings[index] = newSetting;
      } else {
        this.popupSettings.push(newSetting);
      }
    });

    if (settings.length > 0) {
      this.audioDisabled = settings[0].disableAllAudio;
    }
  }

  /**
   * Update all our stored application settings with the new setting values
   * @param settings
   */
  private processAppUpdates(settings: BfxAppEventSetting[]) {
    settings.forEach((element) => {
      const newSetting = this.createAppSetting(element);
      const index = this.applicationSettings.findIndex(
        (setting) => setting.type === newSetting.type,
      );
      if (index != -1) {
        this.applicationSettings[index] = newSetting;
      } else {
        this.applicationSettings.push(newSetting);
      }
    });
    if (settings.length > 0) {
      this.audioDisabled = settings[0].disableAllAudio;
    }
  }

  /**
   * Create list of filters for each setting type
   */
  private initializeFilterGroupMap() {
    this.filterGroupMap.set(
      FilterClassType.ALGO_FILTER,
      this.filtersList.filter(
        (a) =>
          a.blotterType == CubiqTS.BlotterType.ORDER &&
          a.orderFilterType === ExtendedOrderTypeIdentifier.ALGO,
      ),
    );
    this.filterGroupMap.set(
      FilterClassType.BM_FILTER,
      this.filtersList.filter((a) => a.blotterType == CubiqTS.BlotterType.BENCHMARK),
    );
    this.filterGroupMap.set(
      FilterClassType.LIMIT_FILTER,
      this.filtersList.filter(
        (a) =>
          a.blotterType == CubiqTS.BlotterType.ORDER &&
          a.orderFilterType === ExtendedOrderTypeIdentifier.OTHER,
      ),
    );
    this.filterGroupMap.set(
      FilterClassType.TRADE_FILTER,
      this.filtersList.filter((a) => a.blotterType == CubiqTS.BlotterType.TRADE),
    );
    this.filterGroupMap.set(
      FilterClassType.DEALER_INTERVENTION,
      this.filtersList.filter((a) => a.blotterType == CubiqTS.BlotterType.INTERVENTION),
    );
    this.filterGroupMap.set(FilterClassType.NONE, []);
    for (const filters of this.filterGroupMap.values()) {
      filters.unshift(SettingsConst.NONE_FILTER);
    }
  }

  private populateSoundsList(): Promise<SettingAudioType[]> {
    return new Promise<any>((resolve, reject) => {
      this.soundsService.isPrimed().then(() => {
        this.soundsService.getSoundsList().then((soundsList) => {
          const retList = soundsList.map((sound: Sound) => {
            return { label: sound.soundName, value: sound.path };
          });
          resolve(retList);
        });
      });
    });
  }

  private populateFilterList(): Promise<SettingFilterType[]> {
    return new Promise<any>((resolve, reject) => {
      this._filterService.getItems(FilterType.BlotterFilter).then((filtersList) => {
        const retList: SettingFilterType[] = filtersList.map((filter) => {
          let orderFilterType = filter.extendedTypeId;
          if (typeof orderFilterType === "string") {
            orderFilterType = Number(orderFilterType);
          } else if (orderFilterType == null) {
            orderFilterType = ExtendedOrderTypeIdentifier.OTHER;
          }
          let blotterType =
            orderFilterType === ExtendedOrderTypeIdentifier.BENCHMARK &&
            filter["preload"].blotterType === 0
              ? CubiqTS.BlotterType.BENCHMARK
              : (filter["preload"].blotterType as CubiqTS.BlotterType);

          return {
            name: filter.filterName,
            id: filter.filterId,
            blotterType: blotterType,
            orderFilterType: orderFilterType,
          };
        });
        resolve(retList);
      });
    });
  }

  public searchAudios(event): SettingAudioType[] {
    let query: string = event.query.toLowerCase();
    return this.soundsList.filter((a) => a.label.toLowerCase().indexOf(query) >= 0);
  }

  public getNoSound(): SettingAudioType {
    return SettingsConst.NONE_SOUND;
  }
  public getNoFilter(): SettingFilterType {
    return SettingsConst.NONE_FILTER;
  }

  public findSound(soundId: string): SettingAudioType {
    let retSound = SettingsConst.NONE_SOUND;
    if (soundId) {
      retSound = this.soundsList.find(
        (sound) => sound.label.toLowerCase() == soundId.toLowerCase(),
      );
      if (!retSound) {
        retSound = SettingsConst.NONE_SOUND;
      }
    }
    return retSound;
  }

  public getSoundsList(): SettingAudioType[] {
    return this.soundsList.concat();
  }

  public getFilterList(eventType: BFXNotificationType): SettingFilterType[] {
    let filterClass: FilterClassType;
    switch (eventType) {
      case BFXNotificationType.ALGO_DEAL_FAILURE:
      case BFXNotificationType.DEAL_FAILURE:
      case BFXNotificationType.FILLED:
      case BFXNotificationType.PART_FILLED:
      case BFXNotificationType.ALGO_FILLED:
      case BFXNotificationType.ALGO_PART_FILLED:
        filterClass = FilterClassType.TRADE_FILTER;
        break;
      case BFXNotificationType.DEALER_INTERVENTION:
        filterClass = FilterClassType.DEALER_INTERVENTION;
        break;
      case BFXNotificationType.CONNECTION_MONITOR:
      case BFXNotificationType.STREAM_OFF:
        filterClass = FilterClassType.NONE;
        break;
      case BFXNotificationType.BMARK_FILLED:
      case BFXNotificationType.BMARK_SOFT_CUT_OFF:
      case BFXNotificationType.BMARK_HARD_CUT_OFF:
      case BFXNotificationType.BMARK_TRIGGERED:
      case BFXNotificationType.BMARK_NEW_ORDERS:
      case BFXNotificationType.BMARK_DEAL_FAILURE:
      case BFXNotificationType.BMARK_CANCELLED:
      case BFXNotificationType.BMARK_MODIFIED:
      case BFXNotificationType.BMARK_ORDER_ENTERED:
        filterClass = FilterClassType.BM_FILTER;
        break;
      case BFXNotificationType.ALGO_ACCEPTANCE_NEEDED:
      case BFXNotificationType.ALGO_ACTIVATED:
      case BFXNotificationType.ALGO_ACTIVATING:
      case BFXNotificationType.ALGO_CANCELLED:
      case BFXNotificationType.ALGO_EXPIRED:
      case BFXNotificationType.ALGO_EXPIRING:
      case BFXNotificationType.ALGO_LIMITALGO_ORDER_ENTERED:
      case BFXNotificationType.ALGO_MODIFIED:
      case BFXNotificationType.ALGO_UNSOLICITED_CANCEL:
      case BFXNotificationType.ALGO_UNSOLICITED_PAUSE_RESUME:
        filterClass = FilterClassType.ALGO_FILTER;
        break;
      default:
        filterClass = FilterClassType.LIMIT_FILTER;
        break;
    }
    return this.filterGroupMap.get(filterClass) ?? [];
  }

  public getAppFilterList(eventType: BFXAppNotificationType): SettingFilterType[] {
    let filterClass: FilterClassType;
    switch (eventType) {
      case BFXAppNotificationType.ACTIVATED:
      case BFXAppNotificationType.CANCELLED:
      case BFXAppNotificationType.ENTERED:
      case BFXAppNotificationType.EXPIRED:
      case BFXAppNotificationType.MODIFIED:
      case BFXAppNotificationType.SUSPENDED:
        filterClass = FilterClassType.LIMIT_FILTER;
        break;
      case BFXAppNotificationType.ALGO_ACTIVATED:
      case BFXAppNotificationType.ALGO_CANCELLED:
      case BFXAppNotificationType.ALGO_EXPIRED:
      case BFXAppNotificationType.ALGO_MODIFIED:
      case BFXAppNotificationType.ALGO_ENTERED:
        filterClass = FilterClassType.ALGO_FILTER;
        break;
      case BFXAppNotificationType.FILLED:
      case BFXAppNotificationType.PART_FILLED:
      case BFXAppNotificationType.ALGO_FILLED:
      case BFXAppNotificationType.ALGO_PART_FILLED:
        filterClass = FilterClassType.TRADE_FILTER;
        break;
      case BFXAppNotificationType.APP_BMARK_NEW_ORDERS:
      case BFXAppNotificationType.APP_BMARK_CANCELLED:
      case BFXAppNotificationType.APP_BMARK_MODIFIED:
      case BFXAppNotificationType.APP_BMARK_UNSOLICITED_CANCEL:
      case BFXAppNotificationType.APP_BMARK_ENTERED:
      case BFXAppNotificationType.APP_BMARK_FILLED:
        filterClass = FilterClassType.BM_FILTER;
        break;
      default:
        filterClass = FilterClassType.NONE;
        break;
    }
    return this.filterGroupMap.get(filterClass) ?? [];
  }

  /**
   * Get list of all known popup settings that match the specified filter
   * @param filter
   * @returns
   */
  public getPopupNotificationSettings(
    filter: (BFXNotificationEventType) => boolean,
  ): Promise<BfxPopupEventSetting[]> {
    return this.ready.then(() => this.popupSettings.filter((setting) => filter(setting.type)));
  }

  /**
   * Get list of all known application settings that match the specified filter
   * @param filter
   * @returns
   */
  public getAppNotificationSettings(
    filter: (BFXAppNotificationEventType) => boolean,
  ): Promise<BfxAppEventSetting[]> {
    return this.ready.then(() =>
      this.applicationSettings.filter((setting) => filter(setting.type)),
    );
  }

  public getAudioDisabled(): Promise<boolean> {
    return this.ready.then(() => this.audioDisabled);
  }

  public resetSettings(): Promise<void> {
    this.ready = this.ready.then(() => {
      this.ready = this.initializeSettings();
    });
    return this.ready;
  }

  /**
   * Stores a local change to a popup setting
   * @param setting
   */
  public updatePopupSetting(setting: BfxPopupEventSetting) {
    this.updatedPopupSettingsMap.set(setting.type, setting);
  }

  /**
   * Stores a local change to an App setting
   * @param setting
   */
  public updateAppSetting(setting: BfxAppEventSetting) {
    this.updatedAppSettingsMap.set(setting.type, setting);
  }

  /**
   * Get all popup settings to save - if any settings are locally updated, these are included
   * @param bmark
   * @returns
   */
  getSavePopupSettings(bmark: boolean): BfxPopupEventSetting[] {
    const settingMap: Map<BFXNotificationType, BfxPopupEventSetting> = new Map();
    this.popupSettings
      .filter((setting) => setting.isBmark == bmark)
      .forEach((setting) => settingMap.set(setting.type, setting));
    for (const [key, value] of this.updatedPopupSettingsMap.entries()) {
      if (value.isBmark == bmark) {
        settingMap.set(key, value);
      }
    }
    const retSettings: BfxPopupEventSetting[] = [];
    for (const value of settingMap.values()) {
      retSettings.push(value);
    }
    return retSettings;
  }

  /**
   * Get all app settings to save - if any settings are locally updated, these are included
   * @param bmark
   * @returns
   */
  getSaveAppSettings(bmark: boolean): BfxAppEventSetting[] {
    const settingMap: Map<BFXAppNotificationType, BfxAppEventSetting> = new Map();
    this.applicationSettings
      .filter((setting) => setting.isBmark == bmark)
      .forEach((setting) => settingMap.set(setting.type, setting));
    for (const [key, value] of this.updatedAppSettingsMap.entries()) {
      if (value.isBmark == bmark) {
        settingMap.set(key, value);
      }
    }
    const retSettings: BfxAppEventSetting[] = [];
    for (const value of settingMap.values()) {
      retSettings.push(value);
    }
    return retSettings;
  }
}
